import { serviceApi, withServiceRequestDefaults } from 'Shared/network';
import type { ClickViewingCampaignType } from 'app/viewing/_entities/click';
import type { GetViewupSnsAccountResponseDataDto } from 'viewup-api/v2';
import { ViewingPlatform } from '../../model/types/platform.types';

/** 플랫폼 한 개의 behavior별 "오늘 아직 수행 가능" 여부. true=가능, false=한도 도달. */
export type PlatformViewingAvailability = Partial<Record<ClickViewingCampaignType, boolean>>;

export type GetViewupAccountsRes = {
  accountError?: boolean;
  accountName: string;
  accountPlatform: ViewingPlatform;
  accountUuid: string;
  hasDailyLimit?: boolean;
  isActive: boolean;
  isApproved: boolean;
  viewingAvailability: PlatformViewingAvailability;
  viewingPlatform: ViewingPlatform;
}[];

// v2 응답의 viewingAvailability. 생성 타입(Instagram/Nblog 등)이 재귀 자기참조 스텁이라 신뢰 불가 →
// 런타임 실제 형태(플랫폼 → behavior boolean 맵)를 로컬 타입으로 잡는다.
type RawViewingAvailability = Partial<Record<ViewingPlatform, PlatformViewingAvailability>>;

/**
 * 플랫폼 슬라이스가 "한도 도달"인지. 모든 behavior가 false면 true.
 * `length > 0` 가드 필수 — 빈 슬라이스에 `.every()`만 쓰면 vacuous truth로 true가 되어
 * 선택 가능한 계정을 한도로 오판(fail-open → fail-closed 회귀)한다.
 */
const computeHasDailyLimit = (slice: PlatformViewingAvailability): boolean => {
  const values = Object.values(slice);
  return values.length > 0 && values.every((v) => v === false);
};

export const getViewupAccounts = async (): Promise<GetViewupAccountsRes> => {
  const { data: response } = await serviceApi.snsAccount.getViewupSnsAccountList(
    withServiceRequestDefaults({}),
  );

  const accounts: GetViewupAccountsRes = [];

  response.data.forEach((account) => {
    const baseAccount = createBaseAccount(account);
    const platform = account.platformCategory as unknown as ViewingPlatform;
    // 필드 자체가 없는 응답(구버전 BE·부분 장애)에서도 throw하지 않고 빈 슬라이스로 흘린다
    const availability = (account.viewingAvailability ?? {}) as unknown as RawViewingAvailability;

    // 계정당 플랫폼별로 독립적인 한도 상태를 표현한다 (v1은 계정 단위 isLimit을 복사해 구분 못 했음).
    const pushRow = (rowPlatform: ViewingPlatform) => {
      const slice = availability[rowPlatform] ?? {};
      accounts.push({
        ...baseAccount,
        accountPlatform: rowPlatform,
        hasDailyLimit: computeHasDailyLimit(slice),
        viewingAvailability: slice,
        viewingPlatform: rowPlatform,
      });
    };

    // nblog 계정일 때 nplace, nkin 행도 함께 추가
    if (platform === ViewingPlatform.NBLOG) {
      pushRow(ViewingPlatform.NPLACE);
      pushRow(ViewingPlatform.NKIN);
    }

    pushRow(platform);
  });

  return accounts;
};

const createBaseAccount = (account: GetViewupSnsAccountResponseDataDto) => {
  return {
    accountError: !!account.snsAccountErrorId,
    accountName: account.snsId,
    accountUuid: account.snsAccountUuid,
    isActive: account.isActive,
    isApproved: account.isApproved || false,
  };
};
